import { useRef, useState, type ReactNode } from 'react';
import {
  Alert,
  AlertDescription,
  Button,
  Combobox,
  type ComboboxOption,
  FormField,
  FormFieldError,
  FormFieldHint,
  FormFieldLabel,
  Input,
  RadioCardGroup,
  RadioCardItem,
  Skeleton,
  SuccessScreen,
  SummarySection,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@eureca/ui';
import { ConsultantsMenu } from '@/shared/consultants-menu/ConsultantsMenu';
import { ArrowLeftIcon, RouteIcon } from './icons';

/**
 * DEV-276 — "Criação de Vaga" (Figma Jobs 18356:83817..83885, 18316:82822,
 * 18314:43352). Presentational, três views:
 *   form    — Programa → "Qual será o formato de criação?" → Individual
 *             (Nome + Tipo + aviso) | Em massa (Planilha de vagas + modelo)
 *             → botão "Criar vaga" / "Criar vagas" (desabilitado com Tooltip);
 *   confirm — subtítulo "Confirmação": Resumo, Conflitos (só com recusa),
 *             rodapé Voltar à esquerda / Baixar base de conflitos + Confirmar
 *             criação à direita, colados ao pé da página;
 *   success — só ícone, "Criação em massa feita com sucesso" e "Conferir
 *             Vagas", centrados, sem o cabeçalho do formulário.
 */
export type JobCreateFormat = '' | 'individual' | 'mass';

export interface JobCreateValues {
  programId: string;
  format: JobCreateFormat;
  name: string;
  /** EU-709 D-02 — token PT-BR do tipo (`banco-de-talentos`, `estagio`, …). */
  contractType: string;
}

export interface JobContractTypeOption {
  value: string;
  label: string;
}

export interface JobImportSummaryView {
  programName: string;
  toCreate: number;
  toUpdate: number;
  invalid: number;
  published: number;
}

export type JobCreateStep =
  | { kind: 'form' }
  | { kind: 'confirm'; loading: true }
  | {
      kind: 'confirm';
      loading: false;
      summary: JobImportSummaryView;
      hasConflicts: boolean;
      confirming: boolean;
      error: string | null;
    }
  | { kind: 'success' };

export interface JobCreatePageProps {
  values: JobCreateValues;
  contractTypeOptions: readonly JobContractTypeOption[];
  /**
   * Erros por campo. Inclui o erro do servidor no nome (AC-10), que também
   * desabilita o botão até o nome mudar (AC-06, Figma 18356:83867).
   */
  errors: Record<string, string>;
  isSubmitting: boolean;
  programOptions: ComboboxOption[];
  /** Campo "Programa": carregamento ≠ vazio ≠ erro de API (três estados). */
  programsLoading: boolean;
  programsError: string | null;
  companyLogoUrl?: string | null;
  companyName?: string | null;
  /** Caminho em massa: arquivo escolhido e erro abaixo do campo (AC-16..19). */
  /** `retryable` (DEV-509): erro de envio que não é do arquivo — dá para tentar de novo. */
  file: { name: string | null; error: string | null; retryable?: boolean };
  step: JobCreateStep;
  onFieldChange: (field: keyof JobCreateValues, value: string) => void;
  onFileSelect: (files: FileList | null) => void;
  onDownloadTemplate: () => void;
  onSubmit: () => void;
  onBack: () => void;
  /** Rodapé da Confirmação. */
  onConfirmBack: () => void;
  onConfirm: () => void;
  onDownloadConflicts: () => void;
  /** Tela de sucesso. */
  onGoToJobs: () => void;
}

export const DISABLED_SUBMIT_TOOLTIP = 'Preencha os campos corretamente para continuar';
export const SPREADSHEET_HINT_BEFORE_LINK =
  'Envie em .xlsx, com até 20 MB. Se você já tem a planilha de respostas do formulário de alinhamento, ela já está no padrão e pode ser enviada como está. Caso contrário, ';
export const SPREADSHEET_HINT_LINK = 'baixe a planilha modelo';
export const INDIVIDUAL_NOTICE =
  'Fique tranquilo! Você pode editar essas informações quando quiser.';
export const SUCCESS_TITLE = 'Criação em massa feita com sucesso';
export const PROGRAMS_LOADING_MESSAGE = 'Carregando programas…';
export const PROGRAMS_EMPTY_MESSAGE = 'Nenhum programa encontrado';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * AC-06 — a tela é válida? (o botão só habilita assim). Um erro no campo —
 * inclusive o "Já existe uma vaga com esse nome" vindo do servidor — conta
 * como inválido até o campo mudar.
 */
export function isJobCreateFormValid(
  values: JobCreateValues,
  file: { name: string | null; error: string | null; retryable?: boolean },
  errors: Record<string, string>
): boolean {
  if (!UUID_RE.test(values.programId) || errors.programId) return false;
  if (values.format === 'individual')
    return (
      values.name.trim() !== '' &&
      values.contractType !== '' &&
      !errors.name &&
      !errors.contractType
    );
  // DEV-509 AC-2/AC-3 — erro de envio (rede, servidor) não exige escolher o arquivo de
  // novo; erro do próprio arquivo, sim.
  if (values.format === 'mass')
    return file.name !== null && (file.error === null || file.retryable === true);
  return false;
}

function PageShell({
  subtitle,
  logoUrl,
  companyName,
  onBack,
  backDisabled = false,
  children,
}: {
  subtitle?: string;
  logoUrl?: string | null;
  companyName?: string | null;
  onBack: () => void;
  /** Leitura/gravação em andamento: a seta do cabeçalho não interrompe. */
  backDisabled?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-dvh flex-row bg-muted">
      <ConsultantsMenu activeId="jobs" />
      <main className="flex flex-1 flex-col items-center bg-card px-4 pt-6 pb-16 md:px-8 md:pt-8">
        <div className="flex w-full max-w-[630px] flex-1 flex-col gap-8">
          <header className="flex flex-col gap-8">
            <button
              type="button"
              onClick={onBack}
              disabled={backDisabled}
              aria-disabled={backDisabled || undefined}
              className="flex w-fit flex-row items-center gap-1 text-sm font-medium leading-none text-primary disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="job-create-back"
            >
              <ArrowLeftIcon className="size-3.5" />
              Voltar
            </button>
            <div className="flex flex-col gap-2">
              <div className="flex flex-row items-center gap-2">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt={companyName ? `Logo de ${companyName}` : 'Logo da empresa'}
                    className="size-8 rounded-md object-cover"
                  />
                ) : (
                  <div className="flex size-8 items-center justify-center rounded-md bg-secondary text-secondary-foreground">
                    <RouteIcon className="size-4" />
                  </div>
                )}
                <h1 className="text-3xl font-medium leading-none tracking-tight text-foreground md:text-4xl">
                  Criação de Vaga
                </h1>
              </div>
              {subtitle ? (
                <p
                  className="text-xl font-medium leading-none text-foreground"
                  data-testid="job-create-subtitle"
                >
                  {subtitle}
                </p>
              ) : null}
            </div>
          </header>
          {children}
        </div>
      </main>
    </div>
  );
}

/** Figma 18314:43352 — sem cabeçalho: o conteúdo fica centrado na área útil. */
function CenteredShell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-row bg-muted">
      <ConsultantsMenu activeId="jobs" />
      <main className="flex flex-1 flex-col items-center justify-center bg-card px-4 py-8 md:px-8">
        <div className="flex w-full max-w-[630px] flex-1 flex-col items-center justify-center">
          {children}
        </div>
      </main>
    </div>
  );
}

/**
 * AC-07 — Tooltip "Preencha os campos corretamente para continuar" ACIMA do
 * botão desabilitado (Figma 18356:83838: fundo muted, 12px, uma linha, caret
 * de 10px a 45° apontando o botão). O Radix não dispara em `disabled`, então o
 * gatilho é um wrapper focável com `aria-disabled` que segue recebendo hover,
 * foco e clique/toque (padrão EU-560 / ActionCard); o botão em si fica
 * `disabled` nativo. O clique no gatilho ABRE o tooltip em vez de fechá-lo
 * (anotação Dev do frame: "caso o usuário tente clicar...").
 */
function SubmitButton({
  label,
  disabled,
  loading,
  onClick,
}: {
  label: string;
  disabled: boolean;
  loading: boolean;
  onClick: () => void;
}) {
  const [open, setOpen] = useState(false);
  if (!disabled) {
    return (
      <Button
        type="submit"
        size="lg"
        loading={loading}
        data-testid="job-create-submit"
        onClick={onClick}
      >
        {label}
      </Button>
    );
  }
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger
          asChild
          onClick={(event) => {
            // Sem isto o Radix fecha o tooltip no clique; o toque precisa abrir.
            event.preventDefault();
            setOpen(true);
          }}
        >
          <span
            tabIndex={0}
            aria-disabled="true"
            className="inline-flex w-fit cursor-not-allowed"
            data-testid="job-create-submit-disabled-wrapper"
          >
            <Button
              type="button"
              size="lg"
              disabled
              className="pointer-events-none"
              data-testid="job-create-submit"
            >
              {label}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          variant="muted"
          className="shadow-xs"
          data-testid="job-create-submit-tooltip"
        >
          {DISABLED_SUBMIT_TOOLTIP}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function FormView(props: JobCreatePageProps) {
  const { values, errors, isSubmitting, file } = props;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const valid = isJobCreateFormValid(values, file, errors);
  const submitLabel = values.format === 'mass' ? 'Criar vagas' : 'Criar vaga';
  // Três estados distintos na lista do seletor: carregando, vazio, falha de API.
  const programsListMessage =
    props.programsError ??
    (props.programsLoading ? PROGRAMS_LOADING_MESSAGE : PROGRAMS_EMPTY_MESSAGE);

  return (
    <form
      className="flex flex-1 flex-col gap-5"
      onSubmit={(event) => {
        event.preventDefault();
        if (valid && !isSubmitting) props.onSubmit();
      }}
      noValidate
    >
      <div className="flex flex-1 flex-col gap-5">
        <FormField error={Boolean(errors.programId)}>
          <FormFieldLabel className="text-foreground">Programa</FormFieldLabel>
          <Combobox
            options={props.programOptions}
            value={values.programId || undefined}
            onSelect={(value) => props.onFieldChange('programId', value)}
            placeholder="Selecione o programa"
            searchPlaceholder="Busque por programas"
            emptyMessage={programsListMessage}
            error={Boolean(errors.programId)}
            disabled={isSubmitting}
            data-testid="job-create-program"
          />
          <FormFieldError className="text-sm leading-5 text-muted-foreground">
            {errors.programId}
          </FormFieldError>
          {props.programsError ? (
            <p
              role="alert"
              className="text-sm leading-5 text-destructive"
              data-testid="job-create-program-error"
            >
              {props.programsError}
            </p>
          ) : null}
        </FormField>

        {/* AC-01/03/04 — o formato troca o bloco seguinte e o rótulo do botão. */}
        <FormField>
          <FormFieldLabel className="text-foreground">
            Qual será o formato de criação?
          </FormFieldLabel>
          <RadioCardGroup
            value={values.format || undefined}
            onValueChange={(value) => props.onFieldChange('format', value)}
            disabled={isSubmitting}
            className="flex flex-row flex-wrap gap-2"
            data-testid="job-create-format"
          >
            <RadioCardItem
              value="individual"
              className="flex-1 px-4 py-4"
              data-testid="job-create-format-individual"
            >
              Individual
            </RadioCardItem>
            <RadioCardItem
              value="mass"
              className="flex-1 px-4 py-4"
              data-testid="job-create-format-mass"
            >
              Em massa
            </RadioCardItem>
          </RadioCardGroup>
        </FormField>

        {values.format === 'individual' ? (
          <div className="flex flex-col gap-5" data-testid="job-create-individual">
            <FormField error={Boolean(errors.name)}>
              <FormFieldLabel className="text-foreground">Nome da Vaga</FormFieldLabel>
              <Input
                value={values.name}
                placeholder="Ex.: Designer de Produtos"
                variant={errors.name ? 'destructive' : 'default'}
                aria-invalid={Boolean(errors.name)}
                onChange={(event) => props.onFieldChange('name', event.target.value)}
                data-testid="job-create-name"
              />
              <FormFieldError className="text-sm leading-5 text-muted-foreground">
                {errors.name}
              </FormFieldError>
            </FormField>
            <FormField error={Boolean(errors.contractType)}>
              <FormFieldLabel className="text-foreground">Tipo de vaga</FormFieldLabel>
              <RadioCardGroup
                value={values.contractType || undefined}
                onValueChange={(value) => props.onFieldChange('contractType', value)}
                disabled={isSubmitting}
                className="flex flex-row flex-wrap gap-3"
                data-testid="job-create-contract-type"
              >
                {props.contractTypeOptions.map((option) => (
                  <RadioCardItem
                    key={option.value}
                    value={option.value}
                    className="group px-4 py-4"
                    data-testid={`job-create-contract-${option.value}`}
                  >
                    {option.label}
                  </RadioCardItem>
                ))}
              </RadioCardGroup>
              <FormFieldError className="text-sm leading-5 text-muted-foreground">
                {errors.contractType}
              </FormFieldError>
            </FormField>
            <Alert className="border-border bg-card">
              <AlertDescription className="text-sm leading-5 text-muted-foreground">
                {INDIVIDUAL_NOTICE}
              </AlertDescription>
            </Alert>
          </div>
        ) : null}

        {values.format === 'mass' ? (
          <div className="flex flex-col gap-5" data-testid="job-create-mass">
            <FormField error={Boolean(file.error)}>
              <FormFieldLabel className="text-foreground">Planilha de vagas</FormFieldLabel>
              <button
                type="button"
                data-testid="job-create-file"
                aria-invalid={Boolean(file.error)}
                onClick={() => fileInputRef.current?.click()}
                className={cn(
                  'flex h-9 w-full flex-row items-center rounded-lg border bg-background px-3 text-left text-sm shadow-2xs',
                  file.error ? 'border-destructive ring-[3px] ring-destructive/40' : 'border-input',
                  file.name ? 'text-foreground' : 'text-muted-foreground'
                )}
              >
                {file.name ?? 'Clique para selecionar um arquivo'}
              </button>
              <input
                ref={fileInputRef}
                data-testid="job-create-file-input"
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                onChange={(event) => {
                  props.onFileSelect(event.target.files);
                  // Permite re-selecionar o MESMO arquivo depois de corrigir a planilha.
                  event.target.value = '';
                }}
              />
              {file.error ? (
                <FormFieldError
                  className="text-sm leading-5 text-muted-foreground"
                  data-testid="job-create-file-error"
                >
                  {file.error}
                </FormFieldError>
              ) : (
                <FormFieldHint
                  className="text-sm leading-5 text-muted-foreground"
                  data-testid="job-create-file-hint"
                >
                  {SPREADSHEET_HINT_BEFORE_LINK}
                  <button
                    type="button"
                    className="font-medium text-primary underline-offset-2 hover:underline"
                    onClick={props.onDownloadTemplate}
                    data-testid="job-create-download-template"
                  >
                    {SPREADSHEET_HINT_LINK}
                  </button>
                  .
                </FormFieldHint>
              )}
            </FormField>
          </div>
        ) : null}
      </div>

      <div className="flex flex-row">
        <SubmitButton
          label={submitLabel}
          disabled={!valid || isSubmitting}
          loading={isSubmitting}
          onClick={() => undefined}
        />
      </div>
    </form>
  );
}

/**
 * Rodapé da Confirmação (Figma 18316:82822): "Voltar" sozinho à esquerda; à
 * direita, "Baixar base de conflitos" (só com recusa, AC-36) e "Confirmar
 * criação" com 8px entre eles. Sem conflito o grupo da direita tem um botão
 * só — nenhum buraco onde o outro estaria. Em carregamento (AC-34) as mesmas
 * posições, tudo indisponível e sem testid (as ações ainda não existem).
 */
function ConfirmFooter({
  loading,
  hasConflicts,
  confirming,
  onBack,
  onDownloadConflicts,
  onConfirm,
}: {
  loading: boolean;
  hasConflicts: boolean;
  confirming: boolean;
  onBack: () => void;
  onDownloadConflicts: () => void;
  onConfirm: () => void;
}) {
  const testId = (id: string) => (loading ? undefined : id);
  return (
    <div className="flex flex-row flex-wrap items-center justify-between gap-2">
      <Button
        type="button"
        variant="outline"
        onClick={onBack}
        disabled={loading || confirming}
        data-testid={testId('job-create-confirm-back')}
      >
        Voltar
      </Button>
      <div
        className="flex flex-row flex-wrap justify-end gap-2"
        data-testid={testId('job-create-confirm-actions')}
      >
        {hasConflicts ? (
          <Button
            type="button"
            variant="secondary"
            onClick={onDownloadConflicts}
            disabled={loading || confirming}
            data-testid={testId('job-create-download-conflicts')}
          >
            Baixar base de conflitos
          </Button>
        ) : null}
        <Button
          type="button"
          onClick={onConfirm}
          disabled={loading}
          loading={confirming}
          data-testid={testId('job-create-confirm-submit')}
        >
          Confirmar criação
        </Button>
      </div>
    </div>
  );
}

function ConfirmView(
  props: JobCreatePageProps & { step: Extract<JobCreateStep, { kind: 'confirm' }> }
) {
  const { step } = props;
  if (step.loading) {
    return (
      <div
        className="flex flex-1 flex-col gap-8"
        data-testid="job-create-confirm-loading"
        aria-busy="true"
      >
        <div className="flex flex-1 flex-col gap-2">
          <Skeleton className="h-7 w-32" />
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-full" />
        </div>
        <ConfirmFooter
          loading
          hasConflicts={false}
          confirming={false}
          onBack={props.onConfirmBack}
          onDownloadConflicts={props.onDownloadConflicts}
          onConfirm={props.onConfirm}
        />
      </div>
    );
  }
  const { summary } = step;
  return (
    <div className="flex flex-1 flex-col gap-8" data-testid="job-create-confirm">
      {/* `flex-1` aqui empurra o rodapé para o pé da página (Figma: Fields flex-1). */}
      <div className="flex flex-1 flex-col gap-8">
        <SummarySection
          title="Resumo"
          alwaysRender
          data-testid="job-create-summary"
          rows={[
            { label: 'Programa', value: summary.programName, testId: 'job-create-summary-program' },
            {
              label: 'Vagas a serem criadas',
              value: String(summary.toCreate),
              testId: 'job-create-summary-create',
            },
            {
              label: 'Vagas atualizadas',
              value: String(summary.toUpdate),
              testId: 'job-create-summary-update',
            },
          ]}
        />
        {step.hasConflicts ? (
          <SummarySection
            title="Conflitos"
            alwaysRender
            data-testid="job-create-conflicts"
            rows={[
              {
                label: 'Vagas inválidas',
                value: String(summary.invalid),
                testId: 'job-create-conflicts-invalid',
              },
              {
                label: 'Vagas já publicadas',
                value: String(summary.published),
                testId: 'job-create-conflicts-published',
              },
            ]}
          />
        ) : null}
        {step.error ? (
          <p
            role="alert"
            className="text-sm leading-5 text-destructive"
            data-testid="job-create-confirm-error"
          >
            {step.error}
          </p>
        ) : null}
      </div>
      <ConfirmFooter
        loading={false}
        hasConflicts={step.hasConflicts}
        confirming={step.confirming}
        onBack={props.onConfirmBack}
        onDownloadConflicts={props.onDownloadConflicts}
        onConfirm={props.onConfirm}
      />
    </div>
  );
}

export function JobCreatePage(props: JobCreatePageProps) {
  const { step } = props;
  if (step.kind === 'success') {
    return (
      <CenteredShell>
        <SuccessScreen
          title={SUCCESS_TITLE}
          paragraphs={[]}
          actionLabel="Conferir Vagas"
          onAction={props.onGoToJobs}
          data-testid="job-create-success"
        />
      </CenteredShell>
    );
  }
  if (step.kind === 'confirm') {
    return (
      <PageShell
        subtitle="Confirmação"
        logoUrl={props.companyLogoUrl}
        companyName={props.companyName}
        onBack={props.onConfirmBack}
        // Leitura/gravação em andamento: a seta não volta ao formulário (a
        // resposta tardia reabriria a Confirmação por cima da tela de envio).
        backDisabled={step.loading || step.confirming}
      >
        <ConfirmView {...props} step={step} />
      </PageShell>
    );
  }
  return (
    <PageShell logoUrl={props.companyLogoUrl} companyName={props.companyName} onBack={props.onBack}>
      <FormView {...props} />
    </PageShell>
  );
}
